import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EventObject } from "@/components/calendar/types";
import { useAuth } from "@/providers/AuthProvider";
import { useSettings } from "@/providers/SettingsProvider";
import { toast } from "sonner";
import { useActiveCalendarAccount } from "./useActiveCalendarAccount";

// Event interface matching the events table structure
export interface Event {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  color: string;
  location?: string;
  url?: string;
  recurrence_rule?: string;
  recurrence_exceptions?: string[];
  status: 'tentative' | 'confirmed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  visibility: 'private' | 'public' | 'team';
  user_id: string;
  organization_uuid: string;
  assigned_to?: string;
  assignee?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  relations?: Record<string, string[]>;
  custom_fields?: Record<string, any>;
  metadata?: Record<string, any>;
  source?: 'local' | 'google' | 'outlook' | 'unipile' | 'other';
  sync_status?: 'pending' | 'syncing' | 'synced' | 'failed' | 'local_only';
  synced_at?: string;
  sync_error?: string;
  provider_id?: string;
  account_id?: string;
  created_at: string;
  updated_at: string;
}

// Extended event data interface for create/update operations
export interface EventCreateData extends Omit<Event, 'id' | 'user_id' | 'organization_uuid' | 'created_at' | 'updated_at' | 'source' | 'sync_status' | 'synced_at' | 'sync_error' | 'provider_id' | 'account_id'> {
  linkedUsers?: string[];
  contact_ids?: string[];
  candidate_ids?: string[];
  external_emails?: Array<{
    email: string;
    display_name?: string;
    attendance_type?: 'required' | 'optional' | 'resource';
  }>;
  timezone?: string;
  conferencing?: {
    provider?: 'google_meet' | 'teams';
    url?: string;
    is_attendees_list_hidden?: boolean;
  };
  recurrence?: string[];
}

// Fetch events
export const useEvents = () => {
  return useQuery({
    queryKey: ['events'],
    queryFn: async (): Promise<Event[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('start_time', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      // If we have events with assignees, fetch the assignee information separately
      if (data && data.length > 0) {
        const assigneeIds = data
          .filter(event => event.assigned_to)
          .map(event => event.assigned_to)
          .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates

        if (assigneeIds.length > 0) {
          try {
            const { data: assigneeData, error: assigneeError } = await supabase
              .rpc('get_multiple_users_display_info', {
                p_user_ids: assigneeIds
              });

            if (!assigneeError && assigneeData) {
              // Create a map of user_id to user info
              const assigneeMap = new Map(
                assigneeData.map(user => [user.user_id, user])
              );

              // Attach assignee info to events
              return data.map(event => ({
                ...event,
                assignee: event.assigned_to ? assigneeMap.get(event.assigned_to) : undefined
              }));
            }
          } catch (assigneeError) {
            console.warn('Failed to fetch assignee information:', assigneeError);
          }
        }
      }

      return data || [];
    },
  });
};

/**
 * Background sync function - syncs an event to external calendar
 * This is fire-and-forget, doesn't block the UI
 */
async function syncEventToCalendar(
  eventId: string,
  accountId: string,
  eventData: {
    title: string;
    start_time: string;
    end_time: string;
    description?: string;
    location?: string;
    all_day?: boolean;
    timezone?: string;
    internal_user_ids?: string[];
    contact_ids?: string[];
    candidate_ids?: string[];
    external_emails?: Array<{
      email: string;
      display_name?: string;
      attendance_type?: 'required' | 'optional' | 'resource';
    }>;
    conferencing?: any;
    recurrence?: string[];
  }
) {
  try {
    console.log('[syncEventToCalendar] Starting sync for event:', eventId);

    // Mark as syncing
    await supabase
      .from('events')
      .update({
        sync_status: 'syncing',
        last_sync_attempt: new Date().toISOString(),
      })
      .eq('id', eventId);

    // Call edge function to create event in external calendar
    const { data, error } = await supabase.functions.invoke(
      `unipile-handler?command=create-event&account_id=${accountId}&db_event_id=${eventId}`,
      {
        method: 'POST',
        body: eventData,
      }
    );

    if (error) {
      console.error('[syncEventToCalendar] Sync failed:', error);

      // Mark as failed
      await supabase
        .from('events')
        .update({
          sync_status: 'failed',
          sync_error: error.message || 'Sync failed',
          last_sync_attempt: new Date().toISOString(),
        })
        .eq('id', eventId);

      return;
    }

    console.log('[syncEventToCalendar] Sync successful:', data);

    // Mark as synced and save provider_id from response
    await supabase
      .from('events')
      .update({
        sync_status: 'synced',
        synced_at: new Date().toISOString(),
        sync_error: null,
        provider_id: data?.event?.id || null,
        account_id: accountId,
      })
      .eq('id', eventId);

  } catch (err) {
    console.error('[syncEventToCalendar] Exception:', err);

    // Mark as failed
    await supabase
      .from('events')
      .update({
        sync_status: 'failed',
        sync_error: err instanceof Error ? err.message : 'Unknown error',
        last_sync_attempt: new Date().toISOString(),
      })
      .eq('id', eventId);
  }
}

/**
 * Background sync for event update
 */
async function syncEventUpdateToCalendar(
  eventId: string,
  accountId: string,
  updates: Partial<EventCreateData>
) {
  try {
    console.log('[syncEventUpdateToCalendar] Syncing update for event:', eventId);

    // Get the event's provider_id
    const { data: event, error: fetchError } = await supabase
      .from('events')
      .select('provider_id')
      .eq('id', eventId)
      .single();

    if (fetchError || !event?.provider_id) {
      console.error('[syncEventUpdateToCalendar] No provider_id found, cannot sync update');
      return;
    }

    await supabase
      .from('events')
      .update({
        sync_status: 'syncing',
        last_sync_attempt: new Date().toISOString(),
      })
      .eq('id', eventId);

    const { data, error } = await supabase.functions.invoke(
      `unipile-handler?command=update-event&account_id=${accountId}&event_id=${event.provider_id}&db_event_id=${eventId}`,
      {
        method: 'POST',
        body: updates,
      }
    );

    if (error) {
      console.error('[syncEventUpdateToCalendar] Sync failed:', error);

      await supabase
        .from('events')
        .update({
          sync_status: 'failed',
          sync_error: error.message || 'Update sync failed',
          last_sync_attempt: new Date().toISOString(),
        })
        .eq('id', eventId);

      return;
    }

    await supabase
      .from('events')
      .update({
        sync_status: 'synced',
        synced_at: new Date().toISOString(),
        sync_error: null,
      })
      .eq('id', eventId);

  } catch (err) {
    console.error('[syncEventUpdateToCalendar] Exception:', err);

    await supabase
      .from('events')
      .update({
        sync_status: 'failed',
        sync_error: err instanceof Error ? err.message : 'Unknown error',
        last_sync_attempt: new Date().toISOString(),
      })
      .eq('id', eventId);
  }
}

/**
 * Deletes event from external calendar AND database via edge function
 */
async function syncEventDeleteToCalendar(eventId: string, accountId: string, providerId: string) {
  console.log('[syncEventDeleteToCalendar] Deleting event:', eventId);

  const { data, error } = await supabase.functions.invoke(
    `unipile-handler?command=delete-event&account_id=${accountId}&event_id=${providerId}&db_event_id=${eventId}`,
    {
      method: 'POST',
    }
  );

  if (error) {
    console.error('[syncEventDeleteToCalendar] Failed to delete:', error);
    throw new Error(error.message || 'Failed to delete event from calendar');
  }

  return data;
}

// Create event - Direct DB insert + background sync
export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { timezone } = useSettings();
  const { data: accountId } = useActiveCalendarAccount();

  return useMutation({
    mutationFn: async (eventData: EventCreateData) => {
      console.log('[useCreateEvent] Creating event:', eventData);

      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();

      if (!userOrg) throw new Error('User not associated with any organization');

      const { linkedUsers, contact_ids, candidate_ids, external_emails, timezone: tz, conferencing, recurrence, ...eventDataWithoutExtras } = eventData;

      // 1. Create event in database immediately (instant feedback)
      const { data: event, error } = await supabase
        .from('events')
        .insert({
          ...eventDataWithoutExtras,
          user_id: user.id,
          organization_uuid: userOrg.organization_uuid,
          source: 'local',
          sync_status: accountId ? 'pending' : 'local_only',
        })
        .select()
        .single();

      if (error) throw new Error(error.message);

      console.log('[useCreateEvent] Event created in DB:', event.id);

      // 2. Link internal users (immediate)
      if (linkedUsers && linkedUsers.length > 0) {
        const userLinks = linkedUsers.map(userId => ({
          event_id: event.id,
          user_id: userId,
          linked_by: user.id,
        }));

        const { error: linkError } = await supabase
          .from('event_users')
          .insert(userLinks);

        if (linkError) {
          console.error('[useCreateEvent] Error linking users:', linkError);
        }
      }

      // 3. Create contact participants (immediate)
      if (contact_ids && contact_ids.length > 0) {
        // Fetch contact details
        const { data: contacts } = await supabase
          .from('contacts')
          .select('id, email, first_name, last_name')
          .in('id', contact_ids);

        if (contacts) {
          const contactParticipants = contacts.map(contact => ({
            event_id: event.id,
            contact_id: contact.id,
            email: contact.email,
            display_name: `${contact.first_name} ${contact.last_name}`.trim(),
            response_status: 'noreply',
            is_organizer: false,
            attendance_type: 'required',
          }));

          const { error: contactParticipantsError } = await supabase
            .from('event_participants')
            .insert(contactParticipants);

          if (contactParticipantsError) {
            console.error('[useCreateEvent] Error creating contact participants:', contactParticipantsError);
            throw new Error(`Failed to add contact participants: ${contactParticipantsError.message}`);
          }
        }
      }

      // 4. Create candidate participants (immediate)
      if (candidate_ids && candidate_ids.length > 0) {
        // Fetch candidate details
        const { data: candidates } = await supabase
          .from('candidates')
          .select('id, email, name')
          .in('id', candidate_ids);

        if (candidates) {
          const candidateParticipants = candidates
            .filter(candidate => candidate.email) // Only include candidates with emails
            .map(candidate => ({
              event_id: event.id,
              candidate_id: candidate.id,
              email: candidate.email,
              display_name: candidate.name,
              response_status: 'noreply',
              is_organizer: false,
              attendance_type: 'required',
            }));

          if (candidateParticipants.length > 0) {
            const { error: candidateParticipantsError } = await supabase
              .from('event_participants')
              .insert(candidateParticipants);

            if (candidateParticipantsError) {
              console.error('[useCreateEvent] Error creating candidate participants:', candidateParticipantsError);
              throw new Error(`Failed to add candidate participants: ${candidateParticipantsError.message}`);
            }
          }
        }
      }

      // 5. Create external email participants (immediate)
      if (external_emails && external_emails.length > 0) {
        const externalParticipants = external_emails.map(ext => ({
          event_id: event.id,
          contact_id: null,
          candidate_id: null,
          email: ext.email,
          display_name: ext.display_name || null,
          response_status: 'noreply',
          is_organizer: false,
          attendance_type: ext.attendance_type || 'required',
        }));

        const { error: participantsError } = await supabase
          .from('event_participants')
          .insert(externalParticipants);

        if (participantsError) {
          console.error('[useCreateEvent] Error creating external participants:', participantsError);
          throw new Error(`Failed to add external guests: ${participantsError.message}`);
        }
      }

      // 6. Trigger background sync to external calendar (non-blocking)
      if (accountId) {
        // Ensure title exists before syncing
        if (!event.title || !event.title.trim()) {
          throw new Error('Event created in DB but title is missing for calendar sync');
        }

        const syncData = {
          title: event.title.trim(),
          description: event.description || undefined,
          start_time: event.start_time,
          end_time: event.end_time,
          all_day: event.all_day,
          location: event.location || undefined,
          timezone: tz || timezone || 'UTC',
          internal_user_ids: linkedUsers,
          contact_ids,
          candidate_ids,
          external_emails,
          conferencing,
          recurrence,
        };

        // Fire-and-forget: don't await
        syncEventToCalendar(event.id, accountId, syncData).catch(err => {
          console.error('[useCreateEvent] Background sync failed:', err);
          // Sync failure is logged, event is still created
        });

        toast.success("Event created, syncing to calendar...");
      } else {
        toast.success("Event created (local only)");
      }

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-linked-users'] });
    },
    onError: (error) => {
      console.error('[useCreateEvent] Error:', error);
      toast.error(error.message);
    },
  });
};

// Update event - Direct DB update + background sync
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { timezone } = useSettings();
  const { data: accountId } = useActiveCalendarAccount();

  return useMutation({
    mutationFn: async ({ id, linkedUsers, contact_ids, candidate_ids, external_emails, timezone: tz, conferencing, recurrence, ...eventData }: Partial<EventCreateData> & { id: string }) => {
      console.log('[useUpdateEvent] Updating event:', id);

      if (!user) throw new Error('User not authenticated');

      // Get current event to check if it's synced
      const { data: currentEvent } = await supabase
        .from('events')
        .select('provider_id, account_id, sync_status')
        .eq('id', id)
        .single();

      // 1. Update event in database immediately
      const { data: updatedEvent, error } = await supabase
        .from('events')
        .update({
          ...eventData,
          sync_status: currentEvent?.provider_id ? 'pending' : 'local_only',
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw new Error(error.message);

      // 2. Handle linkedUsers synchronization (immediate)
      if (linkedUsers !== undefined) {
        // Delete existing links
        await supabase.from('event_users').delete().eq('event_id', id);

        // Insert new links
        if (linkedUsers.length > 0) {
          const userLinks = linkedUsers.map(userId => ({
            event_id: id,
            user_id: userId,
            linked_by: user.id
          }));

          await supabase.from('event_users').insert(userLinks);
        }
      }

      // 3. Handle contact participants (immediate)
      if (contact_ids !== undefined) {
        // Remove existing contact participants
        await supabase.from('event_participants')
          .delete()
          .eq('event_id', id)
          .not('contact_id', 'is', null);

        // Add new contact participants
        if (contact_ids.length > 0) {
          const { data: contacts } = await supabase
            .from('contacts')
            .select('id, email, first_name, last_name')
            .in('id', contact_ids);

          if (contacts) {
            const contactParticipants = contacts.map(contact => ({
              event_id: id,
              contact_id: contact.id,
              email: contact.email,
              display_name: `${contact.first_name} ${contact.last_name}`.trim(),
              response_status: 'noreply',
              is_organizer: false,
              attendance_type: 'required',
            }));

            const { error: contactParticipantsError } = await supabase
              .from('event_participants')
              .insert(contactParticipants);

            if (contactParticipantsError) {
              console.error('[useUpdateEvent] Error creating contact participants:', contactParticipantsError);
              throw new Error(`Failed to add contact participants: ${contactParticipantsError.message}`);
            }
          }
        }
      }

      // 4. Handle candidate participants (immediate)
      if (candidate_ids !== undefined) {
        // Remove existing candidate participants
        await supabase.from('event_participants')
          .delete()
          .eq('event_id', id)
          .not('candidate_id', 'is', null);

        // Add new candidate participants
        if (candidate_ids.length > 0) {
          const { data: candidates } = await supabase
            .from('candidates')
            .select('id, email, name')
            .in('id', candidate_ids);

          if (candidates) {
            const candidateParticipants = candidates
              .filter(candidate => candidate.email) // Only include candidates with emails
              .map(candidate => ({
                event_id: id,
                candidate_id: candidate.id,
                email: candidate.email,
                display_name: candidate.name,
                response_status: 'noreply',
                is_organizer: false,
                attendance_type: 'required',
              }));

            if (candidateParticipants.length > 0) {
              const { error: candidateParticipantsError } = await supabase
                .from('event_participants')
                .insert(candidateParticipants);

              if (candidateParticipantsError) {
                console.error('[useUpdateEvent] Error creating candidate participants:', candidateParticipantsError);
                throw new Error(`Failed to add candidate participants: ${candidateParticipantsError.message}`);
              }
            }
          }
        }
      }

      // 5. Handle external email participants (immediate)
      if (external_emails !== undefined) {
        // Remove existing external participants (those without contact_id or candidate_id)
        await supabase.from('event_participants')
          .delete()
          .eq('event_id', id)
          .is('contact_id', null)
          .is('candidate_id', null);

        // Add new external participants
        if (external_emails.length > 0) {
          const externalParticipants = external_emails.map(ext => ({
            event_id: id,
            contact_id: null,
            candidate_id: null,
            email: ext.email,
            display_name: ext.display_name || null,
            response_status: 'noreply',
            is_organizer: false,
            attendance_type: ext.attendance_type || 'required',
          }));

          const { error: participantsError } = await supabase
            .from('event_participants')
            .insert(externalParticipants);

          if (participantsError) {
            console.error('[useUpdateEvent] Error creating external participants:', participantsError);
            throw new Error(`Failed to add external guests: ${participantsError.message}`);
          }
        }
      }

      // 6. Trigger background sync if event was previously synced
      const effectiveAccountId = currentEvent?.account_id || accountId;
      if (currentEvent?.provider_id && effectiveAccountId) {
        const syncData: any = {};
        if (eventData.title !== undefined) {
          if (eventData.title && eventData.title.trim()) {
            syncData.title = eventData.title.trim();
          }
        }
        if (eventData.description !== undefined) syncData.description = eventData.description;
        if (eventData.start_time !== undefined) {
          syncData.start_time = eventData.start_time;
          syncData.timezone = tz || timezone || 'UTC';
        }
        if (eventData.end_time !== undefined) syncData.end_time = eventData.end_time;
        if (eventData.location !== undefined) syncData.location = eventData.location;
        if (eventData.all_day !== undefined) syncData.all_day = eventData.all_day;
        if (linkedUsers !== undefined) syncData.internal_user_ids = linkedUsers;
        if (contact_ids !== undefined) syncData.contact_ids = contact_ids;
        if (candidate_ids !== undefined) syncData.candidate_ids = candidate_ids;
        if (external_emails !== undefined) syncData.external_emails = external_emails;
        if (conferencing !== undefined) syncData.conferencing = conferencing;
        if (recurrence !== undefined) syncData.recurrence = recurrence;

        // Fire-and-forget
        syncEventUpdateToCalendar(id, effectiveAccountId, syncData).catch(err => {
          console.error('[useUpdateEvent] Background sync failed:', err);
        });

        toast.success("Event updated, syncing changes...");
      } else {
        toast.success("Event updated");
      }

      return updatedEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-linked-users'] });
    },
    onError: (error) => {
      console.error('[useUpdateEvent] Error:', error);
      toast.error(error.message);
    },
  });
};

// Delete event - Direct DB delete + background sync
export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      console.log('[useDeleteEvent] Deleting event:', id);

      // Get event info to check if it's synced
      const { data: event } = await supabase
        .from('events')
        .select('provider_id, account_id, source')
        .eq('id', id)
        .single();

      if (!event) {
        throw new Error('Event not found');
      }

      // If event is synced to external calendar, use edge function (which handles both Unipile + DB deletion)
      if (event.provider_id && event.account_id && event.source === 'unipile') {
        await syncEventDeleteToCalendar(id, event.account_id, event.provider_id);
        // once deleted from unipile, 
        toast.success("Event deleted from calendar");
      } else {
        // Local-only event, delete directly from DB
        const { error } = await supabase
          .from('events')
          .delete()
          .eq('id', id);

        if (error) throw new Error(error.message);
        toast.success("Event deleted");
      }

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-linked-users'] });
    },
    onError: (error) => {
      console.error('[useDeleteEvent] Error:', error);
      toast.error(error.message);
    },
  });
};

// Transform events to EventObject format for calendar
export const useCalendarEvents = () => {
  const { data: events, isLoading, error, refetch } = useEvents();

  const calendarEvents: EventObject[] = (events || []).map((event) => {
    // Convert PostgreSQL timestamp format to ISO 8601 format
    const parsePostgresTimestamp = (timestamp: string, isAllDay: boolean): Date => {
      const isoFormat = timestamp
        .replace(' ', 'T')
        .replace(/([+-]\d{2})$/, '$1:00')
        .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');

      const date = new Date(isoFormat);

      // For all-day events, extract just the UTC date parts to avoid timezone shifts
      if (isAllDay) {
        return new Date(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate()
        );
      }

      return date;
    };

    return {
      id: event.id,
      title: event.title,
      description: event.description || "",
      start: parsePostgresTimestamp(event.start_time, event.all_day),
      end: parsePostgresTimestamp(event.end_time, event.all_day),
      color: event.color,
      location: event.location,
      url: event.url,
      status: event.status,
      priority: event.priority,
      all_day: event.all_day,
      metadata: {
        candidates: event.relations?.candidates || [],
        offers: event.relations?.offers || [],
        companies: event.relations?.companies || [],
        matches: event.relations?.matches || [],
        contacts: event.relations?.contacts || [],
        location: event.location,
        url: event.url,
        status: event.status,
        priority: event.priority,
        visibility: event.visibility,
        all_day: event.all_day,
        recurrence_rule: event.recurrence_rule,
        recurrence_exceptions: event.recurrence_exceptions,
        assigned_to: event.assigned_to,
        assignee: event.assignee,
        custom_fields: event.custom_fields,
        sync_status: event.sync_status,
        synced_at: event.synced_at,
        sync_error: event.sync_error,
      }
    };
  });

  return {
    events: calendarEvents,
    isLoading,
    error,
    refetch
  };
};
